import {ApiResponse, PaginationPage, RawApiResponse} from 'api/api.types';
import {type UpcomingEvent} from 'api/events/events.types';

export const COMP_PASS_CREATION_STATUS = {
    PENDING: 'pending',
    INVALID: 'invalid',
    VALID: 'valid',
} as const;

export const BATCH_COMP_PASS_CREATION_STATUS = {
    PENDING: 'pending',
    SUCCESS: 'success',
    PARTIAL_FAILURE: 'partial_failure',
    FAILURE: 'failure',
    LINK_CREATED: 'link_created',
} as const;

/**
 * How a comp pass is distributed. Only present for Olympia/Ilitch passes.
 * Note: value naming is still TBD on the backend (OCP-3785).
 */
export const DISTRIBUTION_MODE = {
    ASSIGNED: 'assigned',
    SHARED: 'shared',
} as const;

export type CompPassCreationStatus =
    (typeof COMP_PASS_CREATION_STATUS)[keyof typeof COMP_PASS_CREATION_STATUS];

export type BatchCompPassCreationStatus =
    (typeof BATCH_COMP_PASS_CREATION_STATUS)[keyof typeof BATCH_COMP_PASS_CREATION_STATUS];

export type DistributionMode =
    (typeof DISTRIBUTION_MODE)[keyof typeof DISTRIBUTION_MODE];

export interface CompPassErrorObject {
    code: string;
    message: string;
}

export interface CompPassRecipient {
    email: string;
    ccEmail?: string;
    licensePlate?: string;
    phoneNumber?: string;
    quantity: number;
    id?: string;
}

/**
 * Raw entry/exit counts for a comp pass, driving the Total Usage cell. The owning
 * `CompPass.usage` field is `null` when the pass has no usage data yet.
 */
export interface CompPassUsageSummary {
    /* The number of on-site entry scans. */
    enter: number;
    /* The number of on-site exit scans. */
    exit: number;
}

export interface SharedCompPassLinkTransientTimeFrame {
    label: string;
    startDate: string;
    startTime: string;
    endDate: string;
    endTime: string;
}

export interface SharedCompPassLinkRequest {
    events: UpcomingEvent[];
    transientTimeFrames: SharedCompPassLinkTransientTimeFrame[];
    availablePasses: number;
    eventStartsOffset?: string;
    eventEndsOffset?: string;
}

export interface CompPassEventObject {
    /* The ID of the destination where the event is taking place. */
    destinationId: number;
    /* The title of the destination where the event is taking place. */
    destinationTitle: string;
    /* The ID of the event. */
    eventId: number;
    /* The title of the event. */
    eventTitle: string;
    /* The type ID of the event. */
    eventTypeId: number;
}

/**
 * Parameters for fetching comp passes
 */
export interface GetCompPassesParams {
    /* Start date for filtering comp passes */
    fromDate: string;
    /* End date for filtering comp passes */
    toDate: string;
    /* Filter comp passes by event type IDs */
    eventTypeId?: number | number[];
    /* Filter comp passes by destination IDs */
    destinationId?: number | number[];
    /* Page number for pagination */
    page?: number;
    /* Filter comp passes by batch ID */
    compPassBatchId?: number;
    /* Filter comp passes by email */
    email?: string;
}

export interface CompPass {
    /* The unique identifier for the comp pass */
    compPassId: number;
    /** The display ID */
    displayId: string;
    /* The recipient's email */
    email: string;
    /* The link to the comp pass */
    passLink: string | null;
    /* The timestamp when the pass starts */
    startTime: string;
    /* The timestamp when the pass ends */
    endTime: string;
    /* The status of the comp pass */
    status: 'valid' | 'invalid' | 'deleted' | 'pending';
    /* The event object associated with the comp pass. Only present for event comp passes */
    event: CompPassEventObject | null;
    /* The transient pass label. Only present for transient comp passes (event is null). */
    label: string | null;
    /* The ID of the rental associated with the comp pass. Only present if the creation was successful */
    rentalId: number | null;
    /* The ID of the comp pass batch */
    compPassBatchId: number;
    /* Any error details associated with the comp pass */
    errorDetails: string[] | null;
    /* The timestamp when the comp pass was created */
    created: string;
    /* The email of the user who requested the creation of the comp pass */
    requesterEmail: string;
    /* The internal bill-back amount in pennies (integer). Defaults to 0. */
    billBackPennies: number;
    /* The department/person the pass is billed back to. */
    requestedBy: string | null;
    /* How the pass is distributed. Only present for Olympia/Ilitch passes. */
    distributionMode?: DistributionMode;
    /* Entry/exit usage counts. `null` when no usage data yet; omitted for non-usage facilities. */
    usage?: CompPassUsageSummary | null;
}

/**
 * A single comp pass's bill-back / requested-by update.
 */
export interface CompPassUpdate {
    /* The id of the comp pass to update */
    compPassId: number;
    /* The bill-back amount in pennies (integer) */
    billBackPennies: number;
    /* The department/person the pass is billed back to */
    requestedBy: string;
}

/**
 * Parameters for the bulk comp pass update.
 */
export interface UpdateCompPassesParams {
    /* One entry per comp pass being updated */
    updates: CompPassUpdate[];
}

/**
 * A per-pass failure returned in an otherwise successful bulk update.
 */
export interface CompPassUpdateError {
    /* The id of the comp pass that failed to update */
    compPassId: number;
    /* The reason the update failed */
    message: string;
}

/**
 * The result of a bulk comp pass update. A successful (2xx) response can still
 * include `updateErrors` for individual passes (partial success).
 */
export interface UpdateCompPassesResult {
    /* The ids of the comp passes that were successfully updated */
    updatedCompPassIds: number[];
    /* Any per-pass update failures */
    updateErrors: CompPassUpdateError[];
}

/**
 * Raw (snake_case) shape of the bulk update payload as returned by the API,
 * i.e. the object found at `response.data.data`.
 */
export type UpdateCompPassesRawResponse =
    RawApiResponse<UpdateCompPassesResult>;

export interface BatchCompPassType {
    /* The kind of pass in the batch. */
    type: 'event' | 'transient';
    /* The number of events (event batch) or transient time frames (transient batch). */
    count: number;
}

export interface BatchCompPass {
    /* The ID of the comp pass request. */
    compPassRequestId: number;
    /* The timestamp when the batch was created. */
    created: string;
    /* The number of events that the comp passes were created for. */
    eventCount: number;
    /* The breakdown of pass types in the batch (event or transient) with their counts. */
    passTypes: BatchCompPassType[];
    /* The number of comp passes that failed to be created in the batch. */
    failures: number;
    /* The email of the user who requested the creation of the comp passes. */
    requesterEmail: string;
    /* The status of the batch creation. */
    status: BatchCompPassCreationStatus;
    /* How the comp passes in this batch are distributed. */
    distributionMode?: DistributionMode;
    /* The number of successfully created comp passes in the batch. */
    successes: number;
    /* The total number of comp passes requested in the batch. If the number of failures and successes are lower than the total number of passes, then the batch is still being processed. */
    totalPasses: number;
}

export interface CompPassBatchLinkDetail {
    /* The ID of the shared-link pool row. */
    compPassBatchLinkId: string;
    /* The event title or transient timeframe label associated with this link. */
    title: string;
    /* The venue associated with the event, when available. */
    venue?: string;
    /* The pass timeframe start timestamp. */
    passStarts: string;
    /* The pass timeframe end timestamp. */
    passEnds: string;
    /* The timezone used to display this pool's pass timeframe. */
    timezone: string | null;
    /* The number of passes claimed through the link. */
    claimedPasses: number;
    /* The total available passes configured for the link. */
    availablePasses: number;
    /* The number of claimed passes redeemed at least once. */
    redeemedPasses: number;
    /* The number of entries recorded for this link. Null when usage is unavailable. */
    entries: number | null;
    /* The number of exits recorded for this link. Null when usage is unavailable. */
    exits: number | null;
    /* The public URL users can copy and share. */
    sharedLinkUrl: string;
}

export interface CompPassBatchPoolUsage {
    enter: number;
    exit: number;
}

export interface CompPassBatchPoolEventDetails {
    starts: string;
    ends: string;
    startsOffset: string | null;
    endsOffset: string | null;
    destinationId: number;
    destinationTitle: string;
    eventTitle: string;
    eventId: number;
}

export interface CompPassBatchPoolTransientDetails {
    starts: string;
    ends: string;
    timeframeLabel: string;
}

export interface CompPassBatchPool {
    id: string;
    url: string;
    usage: CompPassBatchPoolUsage | null;
    claimed: number;
    redeemed: number;
    maxPasses: number;
    timezone: string | null;
    eventDetails: CompPassBatchPoolEventDetails | null;
    transientDetails: CompPassBatchPoolTransientDetails | null;
}

export interface CompPassBatchResults {
    /** Comp Pass Batch data */
    data: {
        results: BatchCompPass[];
    };
    /** Metadata for pagination */
    meta: PaginationPage;
    /** Any notifications from the API */
    notifications: Record<string, unknown>;
}

export interface CompPassBatchLinkDetailsResults {
    /** Shared-link batch detail data */
    data: {
        results: CompPassBatchLinkDetail[];
    };
    /** Metadata for pagination */
    meta: PaginationPage;
    /** Any notifications from the API */
    notifications: Record<string, unknown> | unknown[];
}

export interface CompPassBatchPoolsResults {
    /** Shared-link pool data */
    data: {
        results: CompPassBatchPool[];
    };
    /** Metadata for pagination */
    meta: PaginationPage;
    /** Any notifications from the API */
    notifications: Record<string, unknown> | unknown[];
}

/**
 * The results after processing the API response
 */
export interface CompPassesResults {
    /** Comp Pass data */
    data: {
        results: CompPass[];
    };
    /** Metadata for pagination */
    meta: PaginationPage;
    /** Any notifications from the API */
    notifications: Record<string, unknown>;
}

/**
 * The raw API response structure
 */
export interface CompPassesData {
    /** The wrapped data response */
    data: CompPassesResults;
}

export interface CompPassBatchesData {
    /** The wrapped data response */
    data: CompPassBatchResults;
}

export interface CompPassBatchLinkDetailsData {
    /** The wrapped data response */
    data: CompPassBatchLinkDetailsResults;
}

export interface CompPassBatchPoolsData {
    /** The wrapped data response */
    data: CompPassBatchPoolsResults;
}

export type CompPassesResponse = RawApiResponse<CompPassesData>;

export type CompPassBatchesResponse = RawApiResponse<CompPassBatchesData>;

export type CompPassBatchLinkDetailsResponse =
    RawApiResponse<CompPassBatchLinkDetailsData>;

export type CompPassBatchPoolsResponse = RawApiResponse<CompPassBatchPoolsData>;

/**
 * Request parameters for the comp pass redemption-details endpoint.
 */
export interface GetCompPassRedemptionDetailsParams {
    /* Page number for pagination */
    page?: number;
    /* Number of rows per page */
    perPage?: number;
}

/**
 * A single entry/exit activity row in the usage-details flyout. Ordered newest
 * first; each side is `null` when there is no corresponding scan (renders "N/A").
 */
export interface CompPassRedemptionRow {
    /* The entry-scan timestamp (tz-aware ISO, facility offset embedded), or null. */
    enter: string | null;
    /* The exit-scan timestamp (tz-aware ISO, facility offset embedded), or null. */
    exit: string | null;
}

/**
 * The `data` payload of the redemption-details response: the received-at `asOf`
 * timestamp plus the activity rows.
 *
 * Modeled as a (non-array) object so `ApiResponse<T>` places it directly at
 * `response.data.data`. Passing the bare `CompPassRedemptionRow[]` array instead
 * would trigger `ApiResponse`'s array branch (`{results: T}`), which drops the
 * sibling `asOf`.
 */
export interface CompPassRedemptionData {
    /* "Timestamps last updated / received at"; facility offset embedded. Drives the flyout's received-at line. */
    asOf: string;
    /* Activity rows, ordered newest first. */
    results: CompPassRedemptionRow[];
}

/**
 * The results after processing the redemption-details API response
 * (i.e. the camelized `response.data`).
 */
export interface CompPassRedemptionResults {
    /** The received-at timestamp + activity rows */
    data: CompPassRedemptionData;
    /** Pagination metadata */
    meta: PaginationPage;
}

/**
 * Raw (snake_case) shape of the redemption-details `data` payload — the object at
 * `response.data.data`. Passed as the `spotheroGet` generic so `ApiResponse` builds
 * the correct `{ data, meta }` envelope around it.
 */
export type CompPassRedemptionResponse = RawApiResponse<CompPassRedemptionData>;

export interface CreateCompPassResponse {
    data: BatchCompPass[];
    meta: PaginationPage;
}

export type RawCreateCompPassResponse = RawApiResponse<BatchCompPass[]>;

export interface CompPassSummaryCount {
    date: string;
    counts: {
        day: number;
        month: number;
        year: number;
    };
}

export type RawCompPassSummaryCountsResponse =
    ApiResponse<CompPassSummaryCount>;
